import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Button from '../Button/Button';
import { DEFAULT_TRANSITION } from '../../../styles/animations/framerGlobalConfig';
import cn from '~modules/cn';
import ChevronRightIcon from '../../../assets/icons/v2design/navigation/chevron-right.svg';

import './dropdown-view.scss';

const DEFAULT_PADDING = 8;

type Props = {
    title?: React.ReactNode;
    addPadding?: boolean;
    className?: string;
    children?: React.ReactNode;
    defaultExpanded?: boolean;
    style?: React.CSSProperties;
    Handler?: JSX.Element;
};

const DropdownView = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
    const [isExpanded, setExpanded] = React.useState(props.defaultExpanded ?? false);

    const { addPadding = true, Handler } = props;

    React.useEffect(() => {
        setExpanded(props.defaultExpanded ?? false);
    }, [props.defaultExpanded]);

    let HandlerAction;

    if (Handler) {
        if (Handler?.props?.onClick) {
            HandlerAction = React.cloneElement(Handler);
        } else {
            HandlerAction = React.cloneElement(Handler, { onClick: () => setExpanded(!isExpanded) });
        }
    }

    const actionHandler = HandlerAction ?? (
        <Button
            type="button"
            buttonType="tertiary"
            buttonWidth="full"
            className="dropdown-view__button"
            icon={<ChevronRightIcon className="dropdown-view__chevron" />}
            onClick={() => setExpanded(!isExpanded)}
        >
            {props.title}
        </Button>
    );

    return (
        <div
            ref={ref}
            className={cn('dropdown-view', props.className, { 'dropdown-view--expanded': isExpanded, 'dropdown-view--add-padding': addPadding })}
            style={props.style}
        >
            {actionHandler}
            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        className="dropdown-view__children"
                        initial={isExpanded ? 'hide' : 'show'}
                        animate="show"
                        exit="hide"
                        transition={DEFAULT_TRANSITION}
                        variants={{
                            show: {
                                opacity: 1,
                                height: 'auto',
                                paddingTop: addPadding ? DEFAULT_PADDING : undefined
                            },
                            hide: {
                                opacity: 0,
                                height: 0,
                                paddingTop: addPadding ? 0 : undefined
                            }
                        }}
                    >
                        {props.children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

DropdownView.displayName = 'DropdownView';

export default DropdownView;
